import Field from 'components/Form/Field';
import {
    FormContextString,
    RequiredRules,
    RequiredType,
} from 'components/Form/types';
import {getRegisterRules} from 'components/Form/utils';
import {useTooltip} from 'components/Tooltip/hook';
import useFormError from 'hooks/useFormErrors';
import {FC, ReactNode, TextareaHTMLAttributes, useRef} from 'react';
import {FieldError, useFormContext} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';

import {useAutosize} from './utils';

export type TextAreaProps<T = HTMLTextAreaElement> = {
    classNameTextArea?: string;
    error?: FieldError;
    helpText?: ReactNode;
    label?: ReactNode;
    name: string;
    onAutoSize?: () => void;
    requiredType?: RequiredType;
    resize?: 'auto';
    rows?: number;
    scrollIntoViewOnError?: boolean;
    type?: never;
} & RequiredRules<TextareaHTMLAttributes<T>>;

const TextArea: FC<TextAreaProps> = ({
    className,
    classNameTextArea,
    disabled,
    error,
    helpText,
    id,
    label,
    markOptional,
    maxLength,
    name,
    required,
    requiredType,
    resize = 'auto',
    rows = 1,
    rules,
    scrollIntoViewOnError,
    ...rest
}) => {
    const {setTooltipRef} = useTooltip();

    const {register} = useFormContext<FormContextString>();

    const registerRules = getRegisterRules({disabled, required, rules});

    const {ref, ...registration} = register(name, registerRules);

    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    useAutosize({
        disabled: resize !== 'auto',
        name,
        ref: internalRef,
    });

    const fieldError = useFormError(name, error);

    return (
        <Field
            className={className}
            disabled={registerRules?.disabled}
            error={fieldError}
            helpText={helpText}
            label={label}
            markOptional={markOptional}
            maxLength={maxLength}
            name={name}
            required={!!registerRules?.required}
            requiredType={requiredType}
            scrollIntoViewOnError={scrollIntoViewOnError}
            type="textarea"
        >
            <textarea
                className={twMerge(
                    'h-fit resize-none',
                    fieldError && 'input-invalid',
                    classNameTextArea,
                )}
                id={id || name}
                maxLength={maxLength}
                {...registration}
                ref={(element) => {
                    ref(element);
                    internalRef.current = element;
                    setTooltipRef(element);
                }}
                rows={rows}
                {...rest}
            />
        </Field>
    );
};

export default TextArea;
